import type React from "react";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import type { PipelineFilters } from "../../../../hooks/use-sales-pipelines-api";
import {
  useCreatePipeline,
  usePipelineSummary,
  useSalesPipelines,
  useUpdatePipelineStage,
} from "../../../../hooks/use-sales-pipelines-api";
import { getStageBadgeClass, getStageLabel } from "../../utils";
import { PipelineFormModal } from "./PipelineFormModal";

/**
 * ステージ定義
 */
const STAGES = [
  { value: "preparation", label: "提案準備", badgeClass: "badge-ghost" },
  { value: "proposed", label: "提案済", badgeClass: "badge-info" },
  { value: "doc_screening", label: "書類選考", badgeClass: "badge-primary" },
  { value: "interview", label: "面談", badgeClass: "badge-secondary" },
  { value: "offer", label: "オファー", badgeClass: "badge-accent" },
  { value: "won", label: "成約", badgeClass: "badge-success" },
  { value: "lost", label: "失注", badgeClass: "badge-error" },
] as const;

/**
 * 日付が過去かどうかを判定する
 * @param {string | null} dateStr 日付文字列
 * @return {boolean} 過去の日付の場合true
 */
function isOverdue(dateStr: string | null): boolean {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
}

/**
 * ステージ変更時からの経過日数を計算する
 * @param {string | null} stageChangedAt ステージ変更日時
 * @return {number} 経過日数
 */
function calcDaysElapsed(stageChangedAt: string | null): number {
  if (!stageChangedAt) return 0;
  const diff = Date.now() - new Date(stageChangedAt).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

/**
 * パイプラインタブコンポーネント
 * パイプライン一覧とステージサマリーを表示する
 * @return {React.ReactElement} パイプラインタブ
 */
export const PipelineTab: React.FC = () => {
  const [filters, setFilters] = useState<PipelineFilters>({
    page: 1,
    limit: 20,
  });
  const [searchText, setSearchText] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { summary, refetch: refetchSummary } = usePipelineSummary();
  const { pipelines, total, loading, error, refetch } =
    useSalesPipelines(filters);
  const { updateStage } = useUpdatePipelineStage();
  const { createPipeline, loading: creating } = useCreatePipeline();

  const handleCreate = useCallback(
    async (data: Record<string, unknown>) => {
      try {
        await createPipeline(data as never);
        toast.success("パイプラインを追加しました");
        setIsModalOpen(false);
        await refetch();
        await refetchSummary();
      } catch {
        toast.error("パイプラインの追加に失敗しました");
      }
    },
    [createPipeline, refetch, refetchSummary],
  );

  const handleStageFilterChange = useCallback((stage: string) => {
    setFilters(prev => ({ ...prev, stage: stage || undefined, page: 1 }));
  }, []);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(e.target.value);
    },
    [],
  );

  const handleStageChange = useCallback(
    async (pipelineId: string, newStage: string) => {
      try {
        await updateStage(pipelineId, { stage: newStage });
        toast.success("ステージを更新しました");
        await refetch();
      } catch {
        toast.error("ステージの更新に失敗しました");
      }
    },
    [updateStage, refetch],
  );

  // Client-side filter by talent name
  const filteredPipelines = searchText
    ? pipelines.filter(p =>
        p.talentName?.toLowerCase().includes(searchText.toLowerCase()),
      )
    : pipelines;

  return (
    <div>
      {/* Summary bar + Add button */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => setIsModalOpen(true)}
        >
          パイプラインを追加
        </button>
        {STAGES.map(stage => {
          const item = summary.find(s => s.stage === stage.value);
          const count = item?.count ?? 0;
          return (
            <span key={stage.value} className={`badge ${stage.badgeClass}`}>
              {stage.label}: {count}
            </span>
          );
        })}
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          className="select select-bordered select-sm"
          value={filters.stage || ""}
          onChange={e => handleStageFilterChange(e.target.value)}
        >
          <option value="">全ステージ</option>
          {STAGES.map(stage => (
            <option key={stage.value} value={stage.value}>
              {stage.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="タレント名で検索..."
          className="input input-bordered input-sm w-64"
          value={searchText}
          onChange={handleSearchChange}
        />
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-error mb-4">
          <span>データの取得に失敗しました: {error.message}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-8">
          <span className="loading loading-spinner loading-lg" />
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="card bg-base-100 shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>タレント</th>
                  <th>案件</th>
                  <th>ステージ</th>
                  <th>提案単価</th>
                  <th>次アクション</th>
                  <th>経過日数</th>
                  <th>担当</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {filteredPipelines.map(pipeline => {
                  const overdue = isOverdue(pipeline.nextActionDate);
                  const daysElapsed = calcDaysElapsed(pipeline.stageChangedAt);
                  return (
                    <tr key={pipeline.id} className="hover">
                      <td className="font-medium">
                        {pipeline.talentName || "-"}
                      </td>
                      <td>
                        {pipeline.focusMatterTitle ||
                          pipeline.matterTitle ||
                          pipeline.clientCompany ||
                          "-"}
                      </td>
                      <td>
                        <span
                          className={`badge badge-sm ${getStageBadgeClass(pipeline.stage)}`}
                        >
                          {getStageLabel(pipeline.stage)}
                        </span>
                      </td>
                      <td>
                        {pipeline.proposedReward != null
                          ? `${pipeline.proposedReward.toLocaleString()}円`
                          : "-"}
                      </td>
                      <td>
                        <div className="flex flex-col">
                          {pipeline.nextActionDate && (
                            <span
                              className={`text-xs ${overdue ? "text-error font-bold" : ""}`}
                            >
                              {pipeline.nextActionDate}
                            </span>
                          )}
                          {pipeline.nextActionMemo && (
                            <span className="text-xs text-base-content/60">
                              {pipeline.nextActionMemo}
                            </span>
                          )}
                          {!pipeline.nextActionDate &&
                            !pipeline.nextActionMemo &&
                            "-"}
                        </div>
                      </td>
                      <td>{daysElapsed}日</td>
                      <td>{pipeline.assigneeName || "-"}</td>
                      <td>
                        <div className="dropdown dropdown-end">
                          <button
                            type="button"
                            tabIndex={0}
                            className="btn btn-ghost btn-xs"
                          >
                            ステージ変更
                          </button>
                          <ul className="dropdown-content z-10 menu p-2 shadow bg-base-100 rounded-box w-40">
                            {STAGES.map(stage => (
                              <li key={stage.value}>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleStageChange(pipeline.id, stage.value)
                                  }
                                  className={
                                    pipeline.stage === stage.value
                                      ? "active"
                                      : ""
                                  }
                                >
                                  {stage.label}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredPipelines.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-8 text-base-content/60"
                    >
                      パイプラインが見つかりません
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && total > (filters.limit ?? 20) && (
        <div className="flex justify-center mt-4">
          <div className="join">
            <button
              type="button"
              className="join-item btn btn-sm"
              disabled={(filters.page ?? 1) <= 1}
              onClick={() =>
                setFilters(prev => ({ ...prev, page: (prev.page ?? 1) - 1 }))
              }
            >
              &laquo;
            </button>
            <button type="button" className="join-item btn btn-sm" disabled>
              {filters.page ?? 1} / {Math.ceil(total / (filters.limit ?? 20))}
            </button>
            <button
              type="button"
              className="join-item btn btn-sm"
              disabled={
                (filters.page ?? 1) >= Math.ceil(total / (filters.limit ?? 20))
              }
              onClick={() =>
                setFilters(prev => ({ ...prev, page: (prev.page ?? 1) + 1 }))
              }
            >
              &raquo;
            </button>
          </div>
        </div>
      )}

      {/* Create modal */}
      <PipelineFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreate}
        submitting={creating}
      />
    </div>
  );
};
